import { zodResolver } from '@hookform/resolvers/zod';
import { type SetStateAction, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const emailSchema = z.string().email();

function createPracticeInviteEmailsSchema(invalidMessage: string) {
  return z.object({
    emails: z.array(z.string()).superRefine((emails, ctx) => {
      if (
        emails.length === 0 ||
        emails.some((email) => !emailSchema.safeParse(email).success)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: invalidMessage,
        });
      }
    }),
  });
}

type PracticeInviteEmailsFormValues = z.infer<
  ReturnType<typeof createPracticeInviteEmailsSchema>
>;

/** Shared chip email field for Practice invite surfaces (RHF + zod). */
export function usePracticeInviteEmailForm(invalidMessage: string) {
  const schema = useMemo(
    () => createPracticeInviteEmailsSchema(invalidMessage),
    [invalidMessage]
  );

  const form = useForm<PracticeInviteEmailsFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { emails: [] },
    mode: 'onSubmit',
  });

  const [failedEmailsData, setFailedEmailsData] = useState<Record<string, string>>({});

  const emails = form.watch('emails');
  const errorMessage = form.formState.errors.emails?.message ?? null;

  const onEmailsChange = useCallback(
    (value: SetStateAction<string[]>) => {
      const next = typeof value === 'function' ? value(form.getValues('emails')) : value;
      form.setValue('emails', next, { shouldDirty: true });
      form.clearErrors('emails');
      setFailedEmailsData({});
    },
    [form]
  );

  const validateEmails = useCallback(async (): Promise<string[] | null> => {
    const ok = await form.trigger('emails');
    if (!ok) return null;
    return form.getValues('emails');
  }, [form]);

  const setFailedEmails = useCallback(
    (failedEmails: string[], message?: string) => {
      form.setValue('emails', failedEmails, { shouldDirty: true });
      setFailedEmailsData(
        Object.fromEntries(failedEmails.map((email) => [email, message ?? '']))
      );
      if (message !== undefined) {
        form.setError('emails', { type: 'manual', message });
      } else {
        form.clearErrors('emails');
      }
    },
    [form]
  );

  const resetEmailForm = useCallback(() => {
    form.reset({ emails: [] });
    setFailedEmailsData({});
  }, [form]);

  return {
    emails,
    errorMessage,
    failedEmailsData,
    onEmailsChange,
    validateEmails,
    setFailedEmails,
    resetEmailForm,
  };
}
